import { cn } from '@/utils/cn'
import { arcPath, GLYPH_BOX, type GlyphRing, type GlyphSize, voiceGlyph } from './voice-glyph'

export interface VoiceGlyphProps {
  /** The voiceprint's id: the same id always draws the same mark. Never a person's id. */
  id: string
  /** 20 and 28 px beside other things; 40 and 64 px only where one voiceprint stands alone. */
  size?: GlyphSize
  /**
   * `used`: comparisons use this voiceprint. `unused`: it is still being computed or is left out
   * of comparisons — no core, a plain thin circle outside. Always write the reason beside it.
   */
  form?: 'used' | 'unused'
  /** Draw the rings in one after another, once, when the mark appears or turns `used`. */
  drawIn?: boolean
}

const SIZE_CLASS: Record<GlyphSize, string> = {
  20: 'size-5',
  28: 'size-7',
  40: 'size-10',
  64: 'size-16',
}

const CENTRE = GLYPH_BOX / 2

/** The entrance of one ring: its arcs are drawn from their start, about 200 ms a ring. */
const DRAW_IN =
  'transition-[stroke-dashoffset] duration-200 ease-out [stroke-dashoffset:0] starting:[stroke-dashoffset:1] motion-reduce:transition-none'

/** The rings follow one another, the outer one first: about half a second in all. */
const RING_DELAY = ['delay-0', 'delay-150', 'delay-300'] as const

function Ring({ ring, order, drawIn }: { ring: GlyphRing; order: number; drawIn: boolean }) {
  return (
    <g strokeWidth={ring.weight}>
      {ring.arcs.map(arc => (
        <path
          key={arc.start}
          d={arcPath(ring.radius, arc)}
          {...(drawIn ? { pathLength: 1, strokeDasharray: '1 1' } : {})}
          className={cn(drawIn && DRAW_IN, drawIn && RING_DELAY[order])}
        />
      ))}
    </g>
  )
}

/**
 * The mark of one stored voiceprint, the way a portrait stands for a face: rings of arcs around
 * a core, drawn from the voiceprint's id. It says "this voiceprint again" and nothing else — it
 * is not made from the audio, and two similar voices do not get similar marks. So it is never
 * set large beside another one, and it is hidden from assistive technology: the words beside it
 * (its short id, where it came from) carry the meaning.
 *
 * Copper when comparisons use the voiceprint, muted when they do not; the muted form also loses
 * its core and gets a plain circle outside, so the difference never rests on colour.
 */
export function VoiceGlyph({ id, size = 40, form = 'used', drawIn = false }: VoiceGlyphProps) {
  const used = form === 'used'
  const { rings, core, outline } = voiceGlyph(id, size)
  const [outer, ...inner] = rings
  return (
    <svg
      viewBox={`0 0 ${GLYPH_BOX} ${GLYPH_BOX}`}
      aria-hidden
      data-form={form}
      fill="none"
      stroke="currentColor"
      className={cn('shrink-0', SIZE_CLASS[size], used ? 'text-primary' : 'text-muted-foreground')}
    >
      {/* Keyed by form: a mark that turns `used` is drawn anew, so its entrance plays then. */}
      <g key={form} strokeLinecap="round">
        {used ? (
          <Ring ring={outer} order={0} drawIn={drawIn} />
        ) : (
          <circle cx={CENTRE} cy={CENTRE} r={outer.radius} strokeWidth={outline} />
        )}
        {inner.map((ring, index) => (
          <Ring key={ring.radius} ring={ring} order={index + 1} drawIn={drawIn && used} />
        ))}
        {used && (
          <circle
            cx={CENTRE}
            cy={CENTRE}
            r={core}
            className={cn(
              'fill-current stroke-none',
              drawIn &&
                'transition-opacity delay-300 duration-200 starting:opacity-0 motion-reduce:transition-none'
            )}
          />
        )}
      </g>
    </svg>
  )
}
